import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useChapterActionIdMatch from "../../../hooks/useChapterActionIdMatch";

import TableComponent from "../../../components/GeneralComponents/SearchTable/tableComponent";
import AccountManagementModal from "./accountManagementModal/accountManagementModal";
import NotificationWindow from "../../../components/GeneralComponents/NotificationWindow/NotificationWindow";
import SearchBlockRHF from "../../../components/GeneralComponents/searchBlockRHF/SearchBlockRHF";

import {
  blockUser,
  getAccountManagement,
  getAccountManagementsBySurname,
  unBlockUser,
} from "../../../redux/accountManagementReducer/actionsAccountManagement";

import { SEARCH_ACCOUNT_MANAGEMENT_COLUMNS } from "../../../components/GeneralComponents/SearchTable/TablesColumns";
import { options, setOptions } from "./accountManagementConfig";

import styles from "./accountManagementComponent.module.css";

const AccountManagementComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useState({
    surname: "",
    position: null,
    department: null,
    passValidDate: "",
    isBlocked: null,
  });

  const [selectedRowData, setSelectedRowData] = useState(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [typeModal, setTypeModal] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalText, setModalText] = useState("");
  const [showNotificationWindow, setShowNotificationWindow] = useState(false);

  const { allGimsUserPositions, departmentAll } = useSelector((state) => state.dictionaryReducer);

  const accountManagements = useSelector((state) => {
    const { accountManagementReducer } = state;
    return accountManagementReducer.accountManagementsAll;
  });
  const accountManagement = useSelector((state) => {
    const { accountManagementReducer } = state;
    return accountManagementReducer.accountManagement;
  });
  const accountManagementErrorsText = useSelector((state) => {
    const { accountManagementReducer } = state;
    return accountManagementReducer.textError;
  });
  const sortedMenuNames = useSelector((state) => {
    const { roleControllerReducer } = state;
    return roleControllerReducer.sortedMenuNames;
  });

  const blockUserAccess = useChapterActionIdMatch(
    sortedMenuNames?.ACCOUNT_MANAGEMENT?.blockUser?.chapterActionId,
  );
  const editUserAccess = useChapterActionIdMatch(
    sortedMenuNames?.ACCOUNT_MANAGEMENT?.editingUserAccounts?.chapterActionId,
  );
  const createUserAccess = useChapterActionIdMatch(
    sortedMenuNames?.ACCOUNT_MANAGEMENT?.creatingUserAccounts?.chapterActionId,
  );
  const unblockUserAccess = useChapterActionIdMatch(
    sortedMenuNames?.ACCOUNT_MANAGEMENT?.unblockingAUser?.chapterActionId,
  );
  const renewPasswordUserAccess = useChapterActionIdMatch(
    sortedMenuNames?.ACCOUNT_MANAGEMENT?.renewPassword?.chapterActionId,
  );

  const handleChange = (e) => {
    if (e.target) {
      setSearchParams({
        ...searchParams,
        [e.target.dataset.id]: e.target.value,
      });
    } else {
      setSearchParams({ ...searchParams, [e.key]: e.value });
    }
  };

  const handleClickFind = (event) => {
    event.preventDefault();

    setSelectedPage(0);
    setSelectedRowData(null);

    dispatch(getAccountManagementsBySurname(searchParams, 0));
  };

  const handleClickClear = (event) => {
    event.preventDefault();

    setSearchParams(
      structuredClone({
        surname: "",
        position: null,
        department: null,
        passValidDate: "",
        isBlocked: null,
      }),
    );
    setSelectedPage(0);
    setSelectedRowData(null);

    dispatch(getAccountManagementsBySurname({
      surname: "",
      position: null,
      department: null,
      passValidDate: "",
      isBlocked: null,
    },
    0,));
  };

  const handleClickCreateButton = (type) => {
    navigate("/accountManagement/workingPage", { state: { type: type } });
  };

  const handleClickBlockButton = () => {
    setTypeModal("block");
    handleOpenModal(
      "Подтвердить блокировку",
      "Вы уверены, что хотите заблокировать пользователя?",
    );
  };

  const handleClickUnblockButton = () => {
    setTypeModal("unblock");
    handleOpenModal(
      "Подтвердить разблокировку",
      "Вы уверены, что хотите разблокировать пользователя?",
    );
  };

  const handleOpenModal = (title, text) => {
    setShowModal(true);
    setModalTitle(title);
    setModalText(text);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleConfirmAction = (value) => {
    switch (typeModal) {
      case "unblock":
        dispatch(
          unBlockUser(selectedRowData.id, searchParams, selectedPage),
        );
        handleCloseModal();
        setShowNotificationWindow(true);
        setSelectedRowData(null);
        break;
      case "block":
        dispatch(
          blockUser(selectedRowData.id, value, searchParams, selectedPage),
        );
        handleCloseModal();
        setShowNotificationWindow(true);
        setSelectedRowData(null);
        break;
      default:
        break;
    }
  };
  const setNotificationWindowText = (typeNotification) => {
    const notificationWindowText = {
      block:
        accountManagementErrorsText ||
        "Пользователь заблокирован!",
      unblock: accountManagementErrorsText || "Пользователь разблокирован!",
    };
    return notificationWindowText[typeNotification];
  };

  const handleRowSelect = (rowData) => {
    setSelectedRowData(rowData);
    dispatch(getAccountManagement(rowData.id));
  };

  const fetchPageData = (pageIndex) => {
    setSelectedPage(pageIndex);
  };

  useMemo(() => {
    setSelectedRowData(null);
    dispatch(getAccountManagementsBySurname(searchParams, selectedPage));
  }, [selectedPage]);

  useEffect(() => {
    fetchPageData(0);
  }, []);

  useEffect(() => {
    if (allGimsUserPositions.length > 0) {
      setOptions(allGimsUserPositions, 'position');
    }
    if (departmentAll.length > 0) {
      setOptions(departmentAll, 'department');
    }
  }, [allGimsUserPositions, departmentAll]);

  return (
    <>
      <SearchBlockRHF
        options={options.accountManagement}
        handleChange={handleChange}
        handleSearch={handleClickFind}
        handleClear={handleClickClear}
        searchParams={searchParams}
        externalStyles={styles}
      />
      <div className={styles["actions-buttons-block"]}>
        {createUserAccess && (
          <button
            className={styles["save-btn"]}
            onClick={() => handleClickCreateButton("create")}
          >
            Создать
          </button>
        )}
        {editUserAccess && (
          <button
            disabled={!selectedRowData?.id}
            className={styles["save-btn"]}
            onClick={() => handleClickCreateButton("edit")}
          >
            Редактировать
          </button>
        )}
        {renewPasswordUserAccess && (
          <button
            disabled={!selectedRowData?.id}
            className={styles["renew-btn"]}
            onClick={() => handleClickCreateButton("update")}
          >
            Настройки пароля
          </button>
        )}
        {blockUserAccess && (
          <button
            type="block"
            disabled={
              !selectedRowData?.id ||
              Object.keys(accountManagement).length === 0 ||
              (Object.keys(accountManagement).length > 0 &&
                accountManagement.isBlocked)
            }
            className={styles["decline-btn"]}
            onClick={handleClickBlockButton}
          >
            Заблокировать
          </button>
        )}
        {unblockUserAccess && (
          <button
            type="unBlock"
            disabled={
              !selectedRowData?.id ||
              Object.keys(accountManagement).length === 0 ||
              (Object.keys(accountManagement).length > 0 &&
                !accountManagement.isBlocked)
            }
            className={styles["unblock-btn"]}
            onClick={handleClickUnblockButton}
          >
            Разблокировать
          </button>
        )}
      </div>
      <TableComponent
        fetchData={fetchPageData}
        onRowSelect={handleRowSelect}
        columns={SEARCH_ACCOUNT_MANAGEMENT_COLUMNS}
        data={accountManagements.content || []}
        totalPages={accountManagements.totalPages || 0}
        totalElements={accountManagements.totalElements || 0}
        selectedPage={selectedPage}
      />
      <AccountManagementModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirmAction}
        title={modalTitle}
        text={modalText}
        typeModal={typeModal}
      />
      <NotificationWindow
        isError={showNotificationWindow}
        show={showNotificationWindow}
        setShow={setShowNotificationWindow}
        messageText={setNotificationWindowText(typeModal)}
      />
    </>
  );
};

export default AccountManagementComponent;
